import type {
    ConfigureEnhancersCallback,
    DeepPartial,
    Middleware,
    StoreEnhancer,
} from '@reduxjs/toolkit';
import { combineReducers, configureStore } from '@reduxjs/toolkit';

import {
    FLUSH,
    PAUSE,
    PERSIST,
    persistReducer,
    persistStore,
    PURGE,
    REGISTER,
    REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import type { Persistor } from 'redux-persist';
import type { PersistConfig } from 'redux-persist/es/types';
import config from '../moduleConfig';
import { logout, timeout } from '../Slices/Base/base-actions';

export const createStoreWithMiddleware = <RootState>({
    version,
    reducers,
    preloadedState,
    middleware = [],
    enhancers = [],
    transforms = [],
    whitelist,
}: {
    version: string;
    reducers: { [reducerName in keyof Partial<RootState>]: any };
    preloadedState?: DeepPartial<RootState>;
    middleware?: Middleware[];
    enhancers?: StoreEnhancer[] | ConfigureEnhancersCallback;
    transforms?: PersistConfig<RootState>['transforms'];
    /** The slices written to localStorage; every slice when left out. */
    whitelist?: string[];
}) => {
    const rootReducer = combineReducers<RootState>({ ...reducers, ...config.reducers });
    const persistedReducer = persistReducer<RootState>(
        {
            key: `root-${version}`,
            storage,
            transforms,
            whitelist,
            // Merge each saved slice over its initial state, so a field added to a slice since a
            // browser saved it gets its default instead of being undefined (UL-8172). The
            // default (autoMergeLevel1) swaps in the saved slice whole.
            stateReconciler: autoMergeLevel2,
        },
        rootReducer,
    );
    let persistor: Persistor | undefined;
    // When the session ends, drop the stored copy straight away rather than waiting for the reset
    // state to be written over it: a shared machine shouldn't keep the last user's data on disk.
    const purgeOnSessionEnd: Middleware = () => next => action => {
        const result = next(action);
        if (logout.match(action) || timeout.match(action)) {
            persistor?.purge();
        }
        return result;
    };
    const store = configureStore({
        reducer: persistedReducer,
        preloadedState: preloadedState as any,
        enhancers,
        middleware: getDefaultMiddleware => [
            ...getDefaultMiddleware({
                serializableCheck: {
                    ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
                },
            }),
            purgeOnSessionEnd,
            ...middleware,
        ],
    });
    persistor = persistStore(store);

    return { store, persistor };
};
